import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, X, ChevronDown, Check, AlertCircle } from 'lucide-react';

/**
 * Searchable single-select combobox.
 * Props:
 *   options: Array<{ id, name, subtitle? }>
 *   value: id | ''
 *   onChange: (id) => void
 *   placeholder, testId, disabled, error
 */
export default function SearchableSelect({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select…',
  testId,
  disabled = false,
  error = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(() => options.find((o) => o.id === value) || null, [options, value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.name?.toLowerCase().includes(q) ||
        o.subtitle?.toLowerCase().includes(q)
    );
  }, [options, query]);

  const handleSelect = (opt) => {
    onChange?.(opt.id);
    setOpen(false);
    setQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.('');
    setQuery('');
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        data-testid={testId}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
          setTimeout(() => inputRef.current?.focus(), 20);
        }}
        className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-xl text-sm text-left transition-colors ${
          disabled
            ? 'bg-slate-950/40 border border-slate-800/50 text-slate-600 cursor-not-allowed'
            : error
            ? 'bg-slate-950 border border-red-800/70 text-white focus:outline-none'
            : 'bg-slate-950 border border-slate-800 hover:border-slate-700 text-white focus:outline-none focus:border-teal-500'
        }`}
      >
        <span className="truncate">
          {selected ? (
            <span className="font-medium">
              {selected.name}
              {selected.subtitle && (
                <span className="ml-1.5 text-slate-500 text-xs font-normal">· {selected.subtitle}</span>
              )}
            </span>
          ) : (
            <span className="text-slate-500">{placeholder}</span>
          )}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {selected && !disabled && (
            <span
              onClick={handleClear}
              role="button"
              aria-label="Clear selection"
              data-testid={testId ? `${testId}-clear` : undefined}
              className="p-0.5 hover:bg-slate-800 rounded"
            >
              <X className="w-3.5 h-3.5 text-slate-400" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {open && !disabled && (
        <div className="absolute z-30 mt-1.5 w-full bg-slate-900 border border-slate-700 rounded-xl shadow-2xl overflow-hidden">
          <div className="relative border-b border-slate-800">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              ref={inputRef}
              data-testid={testId ? `${testId}-search` : undefined}
              type="text"
              placeholder="Type to filter…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 bg-transparent text-sm text-white placeholder-slate-500 focus:outline-none"
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-4 text-xs text-slate-500 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                No active records match "{query}".
              </div>
            ) : (
              filtered.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={testId ? `${testId}-option-${opt.id}` : undefined}
                  onClick={() => handleSelect(opt)}
                  className="w-full flex items-center justify-between px-3.5 py-2.5 text-left text-sm text-slate-200 hover:bg-slate-800 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium">{opt.name}</div>
                    {opt.subtitle && <div className="truncate text-xs text-slate-500">{opt.subtitle}</div>}
                  </div>
                  {opt.id === value && <Check className="w-4 h-4 text-teal-400 shrink-0 ml-2" />}
                </button>
              ))
            )}
          </div>
          {filtered.length > 0 && (
            <div className="px-3 py-1.5 text-[10px] text-slate-500 bg-slate-950/60 border-t border-slate-800">
              {filtered.length} active record{filtered.length === 1 ? '' : 's'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
